import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/constants/leesritme'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { buttonClasses } from '@/components/generals/button-link'

import {
    LOGIN_LINKS,
    NAV_LINKS,
    VERANTWOORDING_LINKS
} from '@/constants/leesritme'

interface NavDropdownProps {
    label: string
    items: Array<NavItem>
    align?: 'left' | 'right'
}

interface DropdownItemsProps {
    items: Array<NavItem>
    onNavigate?: () => void
}

const DropdownItems: FC<DropdownItemsProps> = ({ items, onNavigate }): ReactNode => {
    return (
        <ul className='grid gap-0.5'>
            {items.map(item => {
                const Icon = item.icon

                return (
                    <li key={item.href}>
                        <a
                            href={item.href}
                            onClick={onNavigate}
                            className='group flex items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-sand focus-visible:bg-sand focus-visible:outline-none'
                        >
                            {Icon && (
                                <span className='mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg bg-leaf-soft text-leaf transition group-hover:bg-white'>
                                    <Icon className='size-4' aria-hidden='true' />
                                </span>
                            )}

                            <span className='min-w-0'>
                                <span className='block font-semibold text-ink'>
                                    {item.label}
                                </span>

                                {item.description && (
                                    <span className='block text-sm leading-snug text-muted-foreground'>
                                        {item.description}
                                    </span>
                                )}
                            </span>
                        </a>
                    </li>
                )
            })}
        </ul>
    )
}

const NavDropdown: FC<NavDropdownProps> = ({ label, items, align = 'left' }): ReactNode => {
    const [open, setOpen] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const onPointer = (event: PointerEvent): void => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setOpen(false)
        }

        const onKey = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return

            setOpen(false)
            buttonRef.current?.focus()
        }

        const onFocus = (event: FocusEvent): void => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setOpen(false)
        }

        document.addEventListener('pointerdown', onPointer)
        document.addEventListener('keydown', onKey)
        document.addEventListener('focusin', onFocus)

        return () => {
            document.removeEventListener('pointerdown', onPointer)
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('focusin', onFocus)
        }
    }, [open])

    return (
        <div ref={wrapperRef} className='relative'>
            <button
                ref={buttonRef}
                type='button'
                aria-expanded={open}
                onClick={() => setOpen(value => !value)}
                className={cn(
                    'inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-[0.9375rem] font-semibold text-ink/80 transition hover:bg-ink/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf',
                    open && 'bg-ink/5 text-ink'
                )}
            >
                {label}
                <ChevronDown className={cn('size-4 transition-transform duration-200', open && 'rotate-180')} aria-hidden='true' />
            </button>

            {open && (
                <div
                    className={cn(
                        'absolute top-full z-50 mt-2 w-[22rem] rounded-2xl border border-border bg-card p-2 shadow-lift animate-in fade-in slide-in-from-top-1 duration-150',
                        align === 'right' ? 'right-0' : 'left-0'
                    )}
                >
                    <DropdownItems items={items} onNavigate={() => setOpen(false)} />
                </div>
            )}
        </div>
    )
}

export const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!mobileOpen) return

        const previous = document.body.style.overflow
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setMobileOpen(false)
        }

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            document.removeEventListener('keydown', onKey)
        }
    }, [mobileOpen])

    const closeMobile = (): void => setMobileOpen(false)

    return (
        <header
            className={cn(
                'sticky top-0 z-40 transition-[background-color,box-shadow,border-color] duration-300',
                mobileOpen
                    ? 'border-b border-ink/10 bg-paper'
                    : scrolled
                        ? 'border-b border-ink/10 bg-paper/90 shadow-[0_8px_30px_-20px_rgb(23_34_56/0.35)] backdrop-blur-md'
                        : 'border-b border-transparent bg-paper/0'
            )}
        >
            <div className='mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between gap-4 px-5 sm:px-8'>
                <Link href='/' aria-label='Leesritme, naar de homepage' className='rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf'>
                    <Logo />
                </Link>

                <nav aria-label='Hoofdmenu' className='hidden items-center gap-0.5 lg:flex'>
                    {NAV_LINKS.map(link => (
                        <a
                            key={link.href}
                            href={link.href}
                            className='rounded-full px-3.5 py-2 text-[0.9375rem] font-semibold text-ink/80 transition hover:bg-ink/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf'
                        >
                            {link.label}
                        </a>
                    ))}

                    <NavDropdown label='Verantwoording' items={VERANTWOORDING_LINKS} />
                    <NavDropdown label='Inloggen' items={LOGIN_LINKS} align='right' />
                </nav>

                <div className='flex items-center gap-2'>
                    <a href='/#aanmelden' className={buttonClasses('primary', 'md', 'hidden sm:inline-flex')}>
                        Start gratis
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </a>

                    <button
                        type='button'
                        onClick={() => setMobileOpen(value => !value)}
                        aria-expanded={mobileOpen}
                        aria-controls='mobiel-menu'
                        aria-label={mobileOpen ? 'Menu sluiten' : 'Menu openen'}
                        className='grid size-11 place-items-center rounded-full border border-ink/15 bg-white/70 text-ink transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf lg:hidden'
                    >
                        {mobileOpen ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div
                    id='mobiel-menu'
                    className='fixed inset-x-0 top-[4.5rem] bottom-0 z-40 overflow-y-auto border-t border-ink/10 bg-paper px-5 pt-4 pb-10 animate-in fade-in slide-in-from-top-2 duration-200 sm:px-8 lg:hidden'
                >
                    <nav aria-label='Mobiel menu' className='mx-auto grid max-w-xl gap-6'>
                        <ul className='grid gap-1'>
                            {NAV_LINKS.map(link => (
                                <li key={link.href}>
                                    <a
                                        href={link.href}
                                        onClick={closeMobile}
                                        className='flex items-center justify-between rounded-xl px-3 py-3 font-heading text-2xl font-semibold text-ink transition hover:bg-sand'
                                    >
                                        {link.label}
                                        <ArrowRight className='size-5 text-ink/40' aria-hidden='true' />
                                    </a>
                                </li>
                            ))}
                        </ul>

                        <div>
                            <p className='px-3 pb-2 text-xs font-bold tracking-[0.14em] text-muted-foreground uppercase'>
                                Verantwoording
                            </p>

                            <DropdownItems items={VERANTWOORDING_LINKS} onNavigate={closeMobile} />
                        </div>

                        <div>
                            <p className='px-3 pb-2 text-xs font-bold tracking-[0.14em] text-muted-foreground uppercase'>
                                Inloggen
                            </p>

                            <DropdownItems items={LOGIN_LINKS} onNavigate={closeMobile} />
                        </div>

                        <a href='/#aanmelden' onClick={closeMobile} className={buttonClasses('primary', 'lg', 'w-full')}>
                            Start gratis met je klas
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </a>
                    </nav>
                </div>
            )}
        </header>
    )
}
